import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, LogIn, Users } from "lucide-react";
import { api, C } from "../../lib/api";
import { AVATAR_CATEGORIES, avatarSrc } from "../../lib/avatars";

const fallbackAvatar = (name) => {
  const people = [...AVATAR_CATEGORIES[0].items, ...AVATAR_CATEGORIES[1].items];
  let h = 0;
  for (const ch of name || "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return people[h % people.length].url;
};

// Lobby friends card: who's online, where they're playing, one tap to join their cash table (or watch a Sit N Go).
export const FriendsList = () => {
  const nav = useNavigate();
  const [friends, setFriends] = useState(null);
  useEffect(() => {
    const load = () => api.get(C.ApiRoutes.MY_FRIENDS).then((r) => setFriends(r.data)).catch(() => setFriends([]));
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  if (!friends) return null;
  const sorted = [...friends].sort((a, b) => (b.online - a.online) || (!!b.location - !!a.location) || a.display_name.localeCompare(b.display_name));
  return (
    <div className="gold-card rounded-2xl p-6 rise" data-testid="friends-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] gold-text"><Users className="w-4 h-4" /> Friends</div>
        <span data-testid="friends-online-count" className="text-[11px] text-slate-400">{friends.filter((f) => f.online).length} online · {friends.length} total</span>
      </div>
      {friends.length === 0 ? (
        <p className="text-xs text-slate-400 mt-3" data-testid="friends-empty">No friends yet — click a player's avatar at any table and hit ADD FRIEND.</p>
      ) : (
        <ul className="mt-4 flex flex-col divide-y divide-white/5 max-h-[320px] overflow-y-auto pane-scroll" data-testid="friends-list">
          {sorted.map((f) => (
            <li key={f.user_id} data-testid={`friend-${f.user_id}`} data-online={f.online ? "true" : "false"} className="flex items-center gap-3 py-2.5">
              <div className="relative shrink-0">
                <img src={f.avatar ? avatarSrc(f.avatar) : fallbackAvatar(f.display_name)} alt={f.display_name} className={`w-10 h-10 rounded-full object-cover bg-slate-800 ${f.online ? "" : "grayscale opacity-60"}`} />
                <span className={`absolute -right-0.5 -bottom-0.5 w-3 h-3 rounded-full border-2 border-slate-900 ${f.online ? "bg-emerald-400 shadow-[0_0_8px_#34d399]" : "bg-slate-500"}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm text-amber-50 truncate">{f.display_name} <span className="rounded-full bg-cyan-500/20 text-cyan-200 text-[9px] font-display px-1.5 py-px">L{f.level}</span></div>
                <div className="text-[11px] text-slate-400 truncate">{f.location ? `Playing ${f.location.name} · ${f.location.stakes}` : f.online ? "In the lobby" : "Offline"}</div>
              </div>
              {f.location && (
                <button data-testid={`friend-join-${f.user_id}`} onClick={() => nav(`/table/${f.location.table_id}`)}
                  className="btn-gold rounded-full px-3 py-1 text-[11px] font-display tracking-wider flex items-center gap-1 btn-lift shrink-0">
                  {f.location.format === "CASH" ? <><LogIn className="w-3 h-3" /> JOIN</> : <><Eye className="w-3 h-3" /> WATCH</>}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
